/**
 * Peças pequenas e repetidas do design system: pill de seleção e a faixa de
 * feedback comum às telas de auth.
 *
 * O botão NÃO mora aqui — está em `Botao.tsx`. Ele saiu porque a versão que
 * vivia neste arquivo era o botão das telas de acesso (52px, raio 10)
 * apresentado como se fosse o botão do sistema (34px, raio 8), e nenhuma tela
 * interna conseguia usá-la.
 */
import type { ReactNode } from 'react'

/**
 * Pill de seleção — canais, tamanhos, categorias, cores e perfis.
 *
 * É um `button` com `aria-pressed`, e não uma `div` clicável: seleção múltipla
 * precisa ser anunciada e alcançável por teclado.
 *
 * O hover é obrigatório mesmo aqui, onde a pill "não parece um botão": numa
 * fileira de quinze tamanhos, é o hover que diz qual deles o clique vai pegar.
 */
export function Pill({
  ativa,
  aoClicar,
  desabilitada = false,
  children,
}: {
  ativa: boolean
  aoClicar: () => void
  desabilitada?: boolean
  children: ReactNode
}) {
  const cores = desabilitada
    ? 'border-borda bg-white text-inativo'
    : ativa
      ? 'border-marca bg-marca-suave text-marca-forte hover:bg-marca-barra'
      : 'border-borda bg-white text-texto hover:border-inativo hover:bg-divisoria'

  return (
    <button
      type="button"
      aria-pressed={ativa}
      disabled={desabilitada}
      onClick={aoClicar}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-[9px] text-[13.5px] font-semibold transition-colors duration-150 ${cores}`}
    >
      {children}
    </button>
  )
}

/** Bolinha de amostra de cor usada nas pills de cor. */
export function AmostraDeCor({ hex }: { hex: string }) {
  return (
    <span
      aria-hidden
      className="inline-block h-[14px] w-[14px] shrink-0 rounded-full border border-borda"
      style={{ background: hex }}
    />
  )
}

/**
 * Faixa de feedback abaixo do conteúdo — verde no sucesso, vermelha no erro.
 *
 * `role="alert"` no erro faz o leitor de tela anunciar sem que o foco precise
 * ir até lá; sucesso usa `status`, que é menos intrusivo.
 */
export function Faixa({ tipo, children }: { tipo: 'sucesso' | 'erro'; children: ReactNode }) {
  const ehErro = tipo === 'erro'

  return (
    <div
      role={ehErro ? 'alert' : 'status'}
      className={`mt-4 rounded-auth px-[14px] py-3 text-[13px] font-medium ${
        ehErro ? 'bg-erro-suave text-erro-texto' : 'bg-ok-suave text-ok-texto'
      }`}
    >
      {children}
    </div>
  )
}

/** Link de texto no padrão da marca. */
export function LinkTexto({
  aoClicar,
  children,
  tamanho = 13.5,
}: {
  aoClicar: () => void
  children: ReactNode
  tamanho?: number
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      style={{ fontSize: tamanho }}
      className="font-semibold text-marca hover:text-marca-forte"
    >
      {children}
    </button>
  )
}
